import { ReactNode } from "react";
import { paymentStripe } from "../../../assests/Icons/icon";
import ThemeButton from "../../theme/ThemeButton";
import styles from "./PropertyPayoutCard.module.scss";

/**
 * Stripe status enum (shared by org & property):
 * 0 Not Setup · 1 Complete · 2 Enabled · 3 Error · 4 Pending (Under Review) · 5 InProgress (Incomplete)
 */
const COMPLETE_STATUSES = [1, 2];
const REVIEW_STATUS = 4;

interface PropertyPayoutCardProps {
  data: any;
  /** Triggers the property-level Stripe onboarding/edit flow (parent's propertyLinkStripeApi). */
  onSetupClick: () => void;
  loading?: boolean;
}

const statusLabel = (status?: number | null) => {
  switch (status) {
    case 1:
      return "Complete";
    case 2:
      return "Enabled";
    case 3:
      return "Error";
    case 4:
      return "Pending";
    case 5:
      return "InProgress";
    default:
      return "Not Setup";
  }
};

const PropertyPayoutCard = ({
  data,
  onSetupClick,
  loading,
}: PropertyPayoutCardProps) => {
  const propStatus = data?.property_stripe_status;
  const propAccountId = data?.property_stripe_connect_account_id;
  const orgStripeStatus = data?.property_organization_details?.stripe_status;

  // Property has a Stripe account in play if it has an account id or a non-zero status
  const hasPropertyStripe =
    !!propAccountId || (propStatus != null && propStatus !== 0);
  const orgStripeActive = COMPLETE_STATUSES.includes(orgStripeStatus);
  const isComplete = COMPLETE_STATUSES.includes(propStatus);
  const isUnderReview = propStatus === REVIEW_STATUS;

  // Compute the card content for the active scenario
  let title: ReactNode;
  let description: ReactNode;
  let buttonText: string | null = null;
  // Tone for the status badge: success | warning | muted
  let statusTone: "success" | "warning" | "muted" = "muted";

  if (hasPropertyStripe) {
    // ---- Scenario A: property-level Stripe account (any status) ----
    if (isComplete) {
      title =
        "Your Property Level Stripe Account is Set Up for Automatic Payouts";
      description =
        "Your property tips will automatically be sent to your Stripe account. You may update your Stripe payment information at any point. Click the button below to do so.";
      buttonText = "Edit Stripe Payment Information";
      statusTone = "success";
    } else if (isUnderReview) {
      title = "Stripe Account Under Review";
      description =
        "Your Stripe account is currently under review. Please check back in sometime. Thank you for your patience.";
      buttonText = "Update Information";
      statusTone = "warning";
    } else {
      title = "Complete Your Stripe Account Setup";
      description =
        "It looks like you started setting up your Stripe account but didn't finish. Please complete the process by clicking the button below.";
      buttonText = "Complete Setup";
      statusTone = "muted";
    }
  } else if (orgStripeActive) {
    // ---- Scenario B: no property Stripe, org has active Stripe ----
    title = (
      <>
        Payout Account:{" "}
        <span className={styles.accountName}>Organization Stripe Account</span>
      </>
    );
    description =
      "This property is configured to receive payouts into the Organization’s Stripe account. You can set up a separate property Stripe account instead.";
    buttonText = "Set Property Stripe Account";
  } else {
    // ---- Scenario C: no property Stripe, no org Stripe → escrow ----
    title = (
      <>
        Payout Account:{" "}
        <span className={styles.accountName}>Organization Escrow Account</span>
      </>
    );
    description =
      "This property’s tips are held in the organization’s escrow account.";
  }

  // Status badge + highlighted account chip are shown for all property-Stripe statuses.
  const showStatusRow = hasPropertyStripe;
  const showFooter = showStatusRow || !!buttonText;

  return (
    <div className={styles.payout_wrap}>
      <div className={styles.top}>
        <div className={styles.logo_box}>
          <span className={styles.innerContent}>{paymentStripe}</span>
        </div>
        <div className={styles.payout_content}>
          <h4>{title}</h4>
          {description ? <p>{description}</p> : null}
        </div>
      </div>

      {showFooter ? (
        <div className={styles.footer}>
          {showStatusRow ? (
            <div className={styles.statusRow}>
              <span className={`${styles.statusBadge} ${styles[statusTone]}`}>
                <span className={styles.statusDot} />
                {statusLabel(propStatus)}
              </span>
              {propAccountId ? (
                <span className={styles.accountChip}>
                  <span className={styles.accountChipLabel}>Account</span>
                  <span className={styles.accountChipValue}>
                    {propAccountId}
                  </span>
                </span>
              ) : null}
            </div>
          ) : (
            <span />
          )}
          {buttonText ? (
            <ThemeButton loading={loading} onClick={onSetupClick}>
              {buttonText}
            </ThemeButton>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default PropertyPayoutCard;
